import React, { useEffect } from 'react';
import { Crown, Sparkles, Check } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import usePremiumGate from '../hooks/usePremiumGate';
import { useLanguage } from '../contexts/LanguageContext';
import { logPaywall } from '../lib/paywallAnalytics';

// Lumera+ tools (dashboard panes + standalone pages) and what each one offers.
export const PREMIUM_TOOLS = {
  maps: { en: ['3D interactive journeys of Paul, Moses & Jesus', 'Terrain flyovers of Bible lands', 'Tap any place to read its verses'], es: ['Viajes 3D interactivos de Pablo, Moisés y Jesús', 'Vuelos sobre el terreno bíblico', 'Toca un lugar para leer sus versículos'] },
  commentary: { en: ['Matthew Henry, Clarke, JFB & Keil-Delitzsch', 'Tyndale Study Notes', 'Compare commentaries side by side'], es: ['Matthew Henry, Clarke, JFB y Keil-Delitzsch', 'Notas de estudio Tyndale', 'Compara comentarios lado a lado'] },
  dictionary: { en: ["Easton's & Smith's Bible dictionaries", 'Every person, place & term explained', 'Linked to the verses where they appear'], es: ['Diccionarios bíblicos Easton y Smith', 'Cada persona, lugar y término explicado', 'Enlazado a los versículos donde aparece'] },
  interlinear: { en: ['Hebrew & Greek word-by-word under each verse', "Strong's numbers & transliteration", 'Tap a word for its full meaning'], es: ['Hebreo y griego palabra por palabra', "Números de Strong y transliteración", 'Toca una palabra para ver su significado'] },
  'original-languages': { en: ['Read the original Hebrew & Greek', 'Root words, grammar & usage', 'See how each word is translated'], es: ['Lee el hebreo y griego originales', 'Raíces, gramática y uso', 'Mira cómo se traduce cada palabra'] },
  isbe: { en: ['The International Standard Bible Encyclopedia', 'In-depth articles on history & culture', 'Searchable and linked to Scripture'], es: ['La Enciclopedia Bíblica Internacional (ISBE)', 'Artículos profundos de historia y cultura', 'Con búsqueda y enlazada a la Escritura'] },
};

export const isPremiumTool = (id) => Object.prototype.hasOwnProperty.call(PREMIUM_TOOLS, id);

/** Blurred preview of a locked Lumera+ tool with an "Unlock" button (starts trial if eligible). */
const PremiumToolLock = ({ toolId, title }) => {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const { user, requirePremium, dialog } = usePremiumGate();
  const es = language === 'es';
  const perks = PREMIUM_TOOLS[toolId]?.[es ? 'es' : 'en'] || [];

  useEffect(() => { logPaywall('shown', title, user ? 'free' : 'guest'); }, [title, user]);

  const unlock = () => {
    logPaywall('clicked', title, user ? 'free' : 'guest');
    if (!user) { navigate('/login?tab=signup'); return; }
    requirePremium(title);
  };

  return (
    <div className="relative overflow-hidden rounded-2xl border-2 border-amber-300/60 dark:border-amber-700/50" data-testid={`tool-premium-lock-${toolId}`}>
      {/* Blurred mock preview of the tool */}
      <div className="pointer-events-none select-none space-y-3 p-6 blur-[6px] opacity-60" aria-hidden="true">
        <div className="h-6 w-1/2 rounded bg-muted" />
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="space-y-2 rounded-xl border p-4">
            <div className="h-4 w-1/3 rounded bg-amber-200/70 dark:bg-amber-800/50" />
            <div className="h-3 w-full rounded bg-muted" />
            <div className="h-3 w-5/6 rounded bg-muted" />
          </div>
        ))}
      </div>
      <div className="absolute inset-0 flex items-center justify-center bg-background/40 p-6">
        <div className="max-w-sm rounded-2xl border bg-card p-6 text-center shadow-xl" data-testid="tool-premium-lock">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-amber-400 to-amber-600 shadow-lg shadow-amber-500/30">
            <Crown className="h-6 w-6 text-white" />
          </div>
          <h2 className="mt-3 text-lg font-bold">{title} · Lumera+</h2>
          <ul className="mt-3 space-y-1.5 text-left text-sm">
            {perks.map((p) => (
              <li key={p} className="flex gap-2"><Check className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />{p}</li>
            ))}
          </ul>
          <Button onClick={unlock} className="mt-4 w-full rounded-full bg-gradient-to-r from-amber-500 to-amber-600 text-white hover:from-amber-600 hover:to-amber-700" data-testid="tool-premium-cta">
            <Sparkles className="mr-2 h-4 w-4" />
            {user ? (es ? 'Desbloquear con Lumera+' : 'Unlock with Lumera+') : (es ? 'Regístrate gratis · 15 días de Lumera+' : 'Sign up free · 15 days of Lumera+')}
          </Button>
        </div>
      </div>
      {dialog}
    </div>
  );
};

export default PremiumToolLock;
